import { getTotalTokens } from '@kyumo/common';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Coins,
  FolderOpen,
  GitBranch,
  Hash,
  Loader2,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  adjustMonth,
  formatCost,
  formatCurrency,
  formatDailyChartData,
  formatMonth,
  formatMonthlyTrendData,
  formatNumber,
  getCurrentMonth,
} from '../../components/admin/chart-utils';
import { MfaResetSection } from '../../components/admin/MfaResetSection';
import { ModelUsagePieCharts } from '../../components/admin/ModelUsagePieCharts';
import { SandboxUsageCard } from '../../components/admin/SandboxUsageCard';
import { SummaryCard } from '../../components/admin/SummaryCard';
import { TaskEndStateBadge } from '../../components/admin/TaskEndStateBadge';
import { TokenBreakdown } from '../../components/admin/TokenBreakdown';
import { DailyCharts, MonthlyTrendCharts } from '../../components/admin/UsageCharts';
import { UserCostLimitSection } from '../../components/admin/UserCostLimitSection';
import { useWebAuth } from '../../contexts/WebAuthContext';
import { useTasks, useUserUsageData } from '../../hooks/use-admin-api';

const TASK_LIMIT_OPTIONS = [10, 25, 50] as const;

/**
 * ユーザー詳細ページ
 */
export function UserDetailPage() {
  const { email } = useParams<{ email: string }>();
  const decodedEmail = email ? decodeURIComponent(email) : '';
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [taskPage, setTaskPage] = useState(0);
  const [taskLimit, setTaskLimit] = useState<number>(10);
  const { user } = useWebAuth();

  // auditグループに所属しているか確認
  const hasAuditGroup = user?.groups?.includes('audit') ?? false;

  // ユーザー使用量データを取得
  const {
    data: usageData,
    isLoading: usageLoading,
    error: usageError,
  } = useUserUsageData(decodedEmail, selectedMonth);

  // ユーザーのタスク一覧を取得（全件）
  // auditグループに所属していない場合はAPIエラーになるが、セクション自体を非表示にするため問題なし
  const { data: tasksData, isLoading: tasksLoading, error: tasksError } = useTasks(decodedEmail);

  const isLoading = usageLoading || (hasAuditGroup && tasksLoading);
  const error = usageError || (hasAuditGroup && tasksError);

  // タスクのページネーション
  const allTasks = tasksData?.tasks || [];
  const taskTotalPages = Math.ceil(allTasks.length / taskLimit);
  const paginatedTasks = useMemo(() => {
    const start = taskPage * taskLimit;
    return allTasks.slice(start, start + taskLimit);
  }, [allTasks, taskPage, taskLimit]);

  const canTaskPrevious = taskPage > 0;
  const canTaskNext = taskPage < taskTotalPages - 1;

  // 月の前後ナビゲーション
  const handlePrevMonth = () => setSelectedMonth((m) => adjustMonth(m, -1));
  const handleNextMonth = () => setSelectedMonth((m) => adjustMonth(m, 1));
  const isCurrentMonth = selectedMonth === getCurrentMonth();

  // 集計データの計算
  const monthlyTotal = usageData?.monthlyData?.[0];
  const inputTokens = monthlyTotal?.inputTokens || 0;
  const cacheReadTokens = monthlyTotal?.cacheReadTokens || 0;
  const cacheWriteTokens = monthlyTotal?.cacheWriteTokens || 0;
  const outputTokens = monthlyTotal?.outputTokens || 0;
  const totalTokens = monthlyTotal ? getTotalTokens(monthlyTotal) : 0;
  const totalCost = monthlyTotal?.cost || 0;
  const totalRequests = monthlyTotal?.requestCount || 0;

  // モデル別使用量（円グラフ用）
  const modelUsage = usageData?.modelUsage || [];

  // 日別データをグラフ用に整形（全日分を0埋め）
  const dailyChartData = formatDailyChartData(usageData?.dailyData || [], usageData?.period);

  // 月別推移データをグラフ用に整形（過去13ヶ月分を0埋め）
  const monthlyTrendData = formatMonthlyTrendData(usageData?.monthlyTrend || [], usageData?.period);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">データの取得に失敗しました</p>
          <p className="text-muted-foreground text-sm mt-2">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* 戻るリンク */}
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        ユーザー一覧に戻る
      </Link>

      {/* ヘッダー */}
      <div
        className="flex flex-col gap-4 mb-8 md:flex-row md:items-center md:justify-between"
        data-testid="page-header"
      >
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground break-all">{decodedEmail}</h1>
          <p className="text-muted-foreground mt-1">ユーザー詳細情報</p>
        </div>

        {/* 月選択 */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="p-3 md:p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-foreground font-medium min-w-32 text-center">
            {formatMonth(selectedMonth)}
          </span>
          <button
            type="button"
            onClick={handleNextMonth}
            disabled={isCurrentMonth}
            className="p-3 md:p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <SummaryCard
          icon={<Zap className="w-6 h-6" />}
          label="総トークン数"
          value={formatNumber(totalTokens)}
          color="blue"
        />
        <SummaryCard
          icon={<Coins className="w-6 h-6" />}
          label="利用料金"
          value={formatCurrency(totalCost)}
          color="yellow"
        />
        <SummaryCard
          icon={<Hash className="w-6 h-6" />}
          label="リクエスト数"
          value={formatNumber(totalRequests)}
          color="green"
        />
      </div>

      {/* コスト上限 */}
      <UserCostLimitSection email={decodedEmail} />

      {/* MFA リセット（認証手段を紛失した利用者の復旧） */}
      <MfaResetSection email={decodedEmail} />

      {/*
        この利用者のサンドボックス使用量。**全体とは別に出す。**
        上限判定は利用者別・全体の双方を見るため、全体だけを見ていると
        「全体は余裕があるのに、この人だけ止まっている」状況が読めない
      */}
      <SandboxUsageCard
        usage={usageData?.userSandboxUsage}
        scope="user"
        isCurrentMonth={isCurrentMonth}
      />

      {/* トークン内訳 */}
      <TokenBreakdown
        inputTokens={inputTokens}
        cacheReadTokens={cacheReadTokens}
        cacheWriteTokens={cacheWriteTokens}
        outputTokens={outputTokens}
      />

      {/* 日別グラフ */}
      <DailyCharts data={dailyChartData} />

      {/* 月別推移グラフ */}
      <MonthlyTrendCharts data={monthlyTrendData} />

      {/* モデル利用割合 */}
      <ModelUsagePieCharts modelUsage={modelUsage} />

      {/* タスク履歴（auditグループ所属時のみ表示） */}
      {hasAuditGroup && (
        <div className="bg-card rounded-xl border border-border">
          <div className="px-4 md:px-6 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">
              タスク履歴 ({allTasks.length} 件)
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[52rem]">
              <thead>
                <tr className="text-left text-muted-foreground text-sm bg-muted/50">
                  <th className="px-6 py-4 font-medium">タイトル</th>
                  <th className="px-6 py-4 font-medium">ワークスペース</th>
                  <th className="px-6 py-4 font-medium">利用料金（合計）</th>
                  <th className="px-6 py-4 font-medium">作成日時</th>
                  <th className="px-6 py-4 font-medium">更新日時</th>
                  <th className="px-6 py-4 font-medium">ステータス</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paginatedTasks.length > 0 ? (
                  paginatedTasks.map((task) => (
                    <tr key={task.id} className="hover:bg-muted/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/users/${encodeURIComponent(decodedEmail)}/tasks/${task.id}`}
                            className="text-foreground hover:text-primary-400 transition-colors font-medium"
                          >
                            {task.title || '無題のタスク'}
                          </Link>
                          {task.versionCount && task.versionCount > 1 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">
                              <GitBranch className="w-3 h-3" />
                              {task.versionCount}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {task.workspaceName ? (
                          <span className="flex items-center gap-1">
                            <FolderOpen className="w-4 h-4" />
                            {task.workspaceName}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-yellow-400">
                        {task.totalCost !== undefined
                          ? formatCost(task.totalCost)
                          : task.cost !== undefined
                            ? formatCost(task.cost)
                            : '-'}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {new Date(task.createdAt).toLocaleString('ja-JP')}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {new Date(task.updatedAt).toLocaleString('ja-JP')}
                      </td>
                      <td className="px-6 py-4">
                        {/* 途切れたタスクだけ理由を出す。削除済みのときは削除済みのバッジを並べる */}
                        <div className="flex flex-wrap items-center gap-1">
                          <TaskEndStateBadge
                            status={task.status}
                            updatedAt={task.updatedAt}
                            lastTurnEnd={task.lastTurnEnd}
                          />
                          {task.deleted && (
                            <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded">
                              削除済み
                              {task.deletedAt && (
                                <span className="ml-1 text-muted-foreground">
                                  ({new Date(task.deletedAt).toLocaleDateString('ja-JP')})
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                      タスクがありません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* タスクページネーション */}
          <div className="px-4 md:px-6 py-4 border-t border-border flex flex-wrap items-center justify-end gap-4 md:gap-6">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">表示</span>
              <select
                value={taskLimit}
                onChange={(e) => {
                  setTaskLimit(Number(e.target.value));
                  setTaskPage(0);
                }}
                className="px-2 py-1 bg-muted border border-border rounded text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {TASK_LIMIT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <span className="text-muted-foreground">件</span>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setTaskPage((p) => p - 1)}
                disabled={!canTaskPrevious}
                className="p-3 md:p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-2 text-sm text-muted-foreground min-w-[60px] text-center">
                {taskTotalPages > 0 ? `${taskPage + 1} / ${taskTotalPages}` : '-'}
              </span>
              <button
                type="button"
                onClick={() => setTaskPage((p) => p + 1)}
                disabled={!canTaskNext}
                className="p-3 md:p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
